import axios from 'axios'

const resolveApiBaseUrl = () => {
  const envBaseUrl = process.env.NEXT_PUBLIC_API_URL || ''

  if (typeof window === 'undefined') return envBaseUrl
  if (process.env.NODE_ENV !== 'development') return envBaseUrl
  if (!envBaseUrl) return envBaseUrl

  try {
    const configuredOrigin = new URL(envBaseUrl, window.location.origin).origin
    if (configuredOrigin !== window.location.origin) {
      return ''
    }
  } catch {
    return ''
  }

  return envBaseUrl
}

const getTimeoutConfig = () => {
  const environment = process.env.NODE_ENV || 'development'
  const customTimeout = process.env.NEXT_PUBLIC_API_TIMEOUT

  const timeouts = {
    development: 300000,
    staging: 300000,
    production: 600000
  }

  return customTimeout
    ? parseInt(customTimeout)
    : timeouts[environment as keyof typeof timeouts] || 300000
}

const apiClient = axios.create({
  baseURL: resolveApiBaseUrl(),
  timeout: getTimeoutConfig(),
  headers: {
    'Content-Type': 'application/json',
  },
})


let refreshTokenPromise: Promise<string | null> | null = null

const isMasterReportAPI = (config: any): boolean => {
  if (!config) return false

  const url = config.url || ''
  const baseURL = config.baseURL || ''
  const fullUrl = baseURL ? `${baseURL}${url}` : url

  return fullUrl.includes('/api/clients/master-report')
}

export const handleAuthFailure = (
  reason: string = 'session_expired'
): void => {

  if (typeof window === 'undefined') return

  localStorage.removeItem('accessToken')

  const isOnLoginPage =
    window.location.pathname === '/login'

  if (!isOnLoginPage) {
    window.location.href =
      `/login?reason=${reason}`
  }
}

/*
 CENTRALIZED REFRESH LOGIC
 Prevents multiple refresh calls
*/
const performTokenRefresh = async (): Promise<string | null> => {

  if (refreshTokenPromise) {
    return refreshTokenPromise
  }

  refreshTokenPromise = (async () => {

    try {

      const refreshResponse =
        await fetch('/api/auth/refresh', {
          method: 'POST',
          credentials: 'include',
        })

      if (!refreshResponse.ok) {

        handleAuthFailure()

        return null
      }

      const data =
        await refreshResponse.json()

      const newToken = data?.token

      if (newToken && typeof window !== 'undefined') {

        localStorage.setItem(
          'accessToken',
          newToken
        )
      }

      return newToken

    } catch (error) {

      console.error(
        'Refresh failed:',
        error
      )

      handleAuthFailure()

      return null

    } finally {

      refreshTokenPromise = null

    }

  })()

  return refreshTokenPromise
}

/*
 REQUEST INTERCEPTOR
*/
apiClient.interceptors.request.use(

  (config) => {

    if (typeof window !== 'undefined') {

      const token =
        localStorage.getItem('accessToken')

      if (token && config.headers) {

        config.headers.Authorization =
          `Bearer ${token}`
      }
    }

    return config
  },

  (error) => Promise.reject(error)

)

/*
 RESPONSE INTERCEPTOR
 Handles ALL APIs including master report
*/
apiClient.interceptors.response.use(

  (response) => response,

  async (error) => {

    const originalRequest = error.config

    if (!originalRequest) {
      return Promise.reject(error)
    }

    /*
     Prevent infinite retry loop
    */
    if (originalRequest._retry) {
      return Promise.reject(error)
    }

    if (error.response?.status === 401) {

      originalRequest._retry = true

      try {

        const newToken =
          await performTokenRefresh()

        if (!newToken) {

          return Promise.reject(error)
        }

        /*
         Preserve request method
        */
        if (!originalRequest.method) {

          originalRequest.method = 'GET'
        }

        /*
         Attach new token
        */
        originalRequest.headers =
          originalRequest.headers || {}

        originalRequest.headers.Authorization =
          `Bearer ${newToken}`

        /*
         Retry request
        */
        return apiClient(originalRequest)

      } catch (refreshError) {

        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  }

)

export default apiClient


/*
 AUTHENTICATED FETCH HELPER
*/
export const makeAuthenticatedRequest = async (

  url: string,
  options: RequestInit = {},
  accessToken?: string

) => {

  const headers: Record<string,string> = {

    'Content-Type':'application/json',

    ...(options.headers as Record<string,string>)
  }

  if (accessToken) {

    headers.Authorization =
      `Bearer ${accessToken}`
  }

  let response = await fetch(url, {

    ...options,
    headers,
    credentials:'include'
  })

  /*
   If token expired
  */
  if (response.status === 401) {

    const newToken =
      await performTokenRefresh()

    if (!newToken) {

      throw new Error(
        'Authentication failed'
      )
    }

    headers.Authorization =
      `Bearer ${newToken}`

    response = await fetch(url,{

      ...options,
      headers,
      credentials:'include'
    })
  }

  if (!response.ok) {

    throw new Error(
      `HTTP error ${response.status}`
    )
  }

  return response
}


/*
 ADMIN REQUEST HELPER
*/
export const makeAdminRequest = async (

  url:string,
  options:RequestInit = {},
  accessToken:string

) => {

  return makeAuthenticatedRequest(

    url,

    {
      ...options,

      headers:{

        Authorization:
          `Bearer ${accessToken}`,

        ...(options.headers as Record<string,string>)
      }

    }

  )
}